"use client";

import { useState } from "react";
import { useTranslations } from "next-intl";

import { Link } from "@/i18n/navigation";
import { ChampionIcon } from "@/shared/ui/data-display/champion-icon";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/shared/ui/overlays/tooltip";

import { useMatchChampionPools } from "../model/use-match-champion-pools";

export interface PoolBadgeProps {
  tournamentId: string;
  matchId: string;
  poolSize: number;
  publishedGames: number;
  className?: string;
}

/**
 * KAN-1676: бейдж «Пулы 6+6» в карточке сетки.
 *
 * Клик ведёт на страницу матча с якорем #pools (без новой модалки), а наведение
 * показывает мини-превью последней выданной игры: запрос пулов уходит только
 * при первом hover, поэтому сетка не делает N запросов на загрузку.
 */
export function PoolBadge({
  tournamentId,
  matchId,
  poolSize,
  publishedGames,
  className,
}: PoolBadgeProps) {
  const t = useTranslations("Bracket.board");
  const [hovered, setHovered] = useState(false);
  const { games } = useMatchChampionPools(matchId, { enabled: hovered });

  const latest = games.length > 0 ? games[games.length - 1] : undefined;
  const blue = latest?.pools.find((pool) => pool.side === "Blue")?.championIds ?? [];
  const red = latest?.pools.find((pool) => pool.side === "Red")?.championIds ?? [];

  return (
    <Tooltip onOpenChange={setHovered}>
      <TooltipTrigger asChild>
        <Link
          href={`/tournaments/${tournamentId}/matches/${matchId}#pools`}
          onClick={(event) => event.stopPropagation()}
          className={
            className ??
            "block border-t border-border bg-primary/5 px-2 py-0.5 text-center text-[10px] font-semibold text-primary transition-colors hover:bg-primary/10"
          }
        >
          {t("poolsBadge", { n: poolSize })}
        </Link>
      </TooltipTrigger>
      <TooltipContent side="top" className="space-y-1 p-2">
        {latest ? (
          <>
            <div className="text-[11px] font-semibold text-foreground">
              {t("poolsTooltipTitle", {
                game: latest.gameNumber,
                games: publishedGames,
              })}
            </div>
            <div className="flex items-center gap-1">
              <span className="w-8 text-[10px] uppercase text-muted-foreground">
                {t("poolsTooltipBlue")}
              </span>
              {blue.map((id) => (
                <ChampionIcon key={id} name={id} label={id} size={20} />
              ))}
            </div>
            <div className="flex items-center gap-1">
              <span className="w-8 text-[10px] uppercase text-muted-foreground">
                {t("poolsTooltipRed")}
              </span>
              {red.map((id) => (
                <ChampionIcon key={id} name={id} label={id} size={20} />
              ))}
            </div>
          </>
        ) : (
          <span className="text-[11px] text-muted-foreground">
            {t("poolsTooltipLoading")}
          </span>
        )}
      </TooltipContent>
    </Tooltip>
  );
}
